import React, { useEffect, useState } from 'react';
import { View, Text, Modal, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Svg, Path, Circle } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { biometricService } from '@/services/biometricService';
import PrimaryButton from '@/components/PrimaryButton';
import TertiaryButton from '@/components/TertiaryButton';

const FaceIdIcon = () => (
  <Svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <Circle cx="24" cy="24" r="22" stroke="#015480" strokeWidth="2" />
    <Path d="M16 18V16.5C16 15.12 17.12 14 18.5 14H20" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M28 14H29.5C30.88 14 32 15.12 32 16.5V18" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M32 30V31.5C32 32.88 30.88 34 29.5 34H28" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M20 34H18.5C17.12 34 16 32.88 16 31.5V30" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Circle cx="19.5" cy="22.5" r="1.5" fill="#015480" />
    <Circle cx="28.5" cy="22.5" r="1.5" fill="#015480" />
    <Path d="M20 29C20 29 21.5 31 24 31C26.5 31 28 29 28 29" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M24 22V26" stroke="#015480" strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

const FingerprintIcon = () => (
  <Svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <Path d="M24 10C16.27 10 10 16.27 10 24" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M24 10C31.73 10 38 16.27 38 24" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M17 24C17 20.13 20.13 17 24 17C27.87 17 31 20.13 31 24C31 29 27 36 24 38" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M24 24V31" stroke="#015480" strokeWidth="2" strokeLinecap="round" />
    <Path d="M13 30C13 30 11 27.2 11 24" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M35 30C35 30 37 27.2 37 24" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M20 36C20 36 17 33 17 28" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

interface Props {
  // When rendered by StartupPromptQueue, these props control the modal externally.
  // Without them, the component reads state directly from AuthContext (legacy behaviour).
  queueVisible?: boolean;
  onQueueDismiss?: () => void;
  onQueueConfirm?: () => void;
}

export default function BiometricEnableModal({ queueVisible, onQueueDismiss, onQueueConfirm }: Props = {}) {
  const { t } = useLanguage();
  const { showBiometricModal, biometricIsFaceId, dismissBiometricModal, confirmBiometricEnrolment } = useAuth();
  const [queueIsFaceId, setQueueIsFaceId] = useState(false);

  const isQueueControlled = queueVisible !== undefined;
  const visible = isQueueControlled ? queueVisible : showBiometricModal;
  const isFaceId = isQueueControlled ? queueIsFaceId : biometricIsFaceId;

  useEffect(() => {
    if (isQueueControlled && queueVisible) {
      biometricService.hasFaceId().then(setQueueIsFaceId).catch(() => {});
    }
  }, [isQueueControlled, queueVisible]);

  const handleDismiss = async () => {
    if (isQueueControlled) {
      onQueueDismiss?.();
    } else {
      dismissBiometricModal();
    }
  };

  const handleConfirm = async () => {
    await confirmBiometricEnrolment();
    if (isQueueControlled) {
      onQueueConfirm?.();
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      statusBarTranslucent
      onRequestClose={handleDismiss}
    >
      <View style={styles.overlay}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={handleDismiss} />
        <View style={styles.sheet}>
          <View style={styles.handle} />

          <View style={styles.iconContainer}>
            {isFaceId ? <FaceIdIcon /> : <FingerprintIcon />}
          </View>

          <Text style={styles.title}>{t('biometric.enableTitle')}</Text>
          <Text style={styles.description}>{t('biometric.enableDescription')}</Text>

          <PrimaryButton
            title={t('biometric.enableButton')}
            onPress={handleConfirm}
          />

          <View style={styles.buttonGap} />

          <TertiaryButton
            title={t('biometric.notNow')}
            onPress={handleDismiss}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(21, 21, 21, 0.5)',
  },
  sheet: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 32,
    paddingTop: 12,
    alignItems: 'center',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#DAD8CF',
    marginBottom: 24,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontFamily: 'Sentient-Regular',
    fontSize: 22,
    lineHeight: 28,
    color: '#151515',
    textAlign: 'center',
    marginBottom: 10,
  },
  description: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: '#7E7D75',
    textAlign: 'center',
    marginBottom: 28,
    paddingHorizontal: 8,
  },
  buttonGap: {
    height: 16,
  },
});
